import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import {
  ArrowDownIcon,
  CurrencyDollarIcon,
  ArrowRightIcon
} from '@heroicons/react/outline'
import { Button } from '@components/UI/Button'
import { AffiliateContactForm } from '@components/AffiliateContactForm'
import FeaturesList from '@components/DomoreportFeaturesList'

export default function Home() {
  return (
    <>
      <NextSeo
        title="Majordomo Pricing | Home Repair Costs Simplified"
        description="Find specific home repair costs tailored to your ZIP Code. Get your report within 24 hours of submitting your home inspection PDF. Learn more."
        canonical="https://majordomo.com/home-repair-pricing"
        openGraph={{
          url: 'https://majordomo.com/home-repair-pricing',
          title: 'Majordomo Pricing | Home Repair Costs Simplified',
          description:
            'Find specific home repair costs tailored to your ZIP Code. Get your report within 24 hours of submitting your home inspection PDF. Learn more.',
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/pricing-hero.png',
              width: 700,
              height: 680,
              alt:
                'A keyboard with a blue archive button and binders sitting on top of the button'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <CurrencyDollarIcon
                width={36}
                height={36}
                className="pr-2 mr-2 border-r border-gray-200 text-brand"
              />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Home Inspection Analysis & Repair Pricing
              </h1>
            </div>

            <p className="w-full max-w-lg md:p-0 px-8 mb-4">
              Majordomo offers a modern tool that provides ZIP Code specific
              repair estimates based on a home inspection.
            </p>

            <p className="w-full max-w-lg md:p-0 px-8 mb-16">
              Not only is this the best tool to simplify the home purchasing
              process, but it also empowers you to create a relationship with
              your home on a firm foundation.
            </p>

            <p className="w-full flex items-center justify-center font-black">
              Learn more about the Domoreport{' '}
              <ArrowDownIcon
                width={20}
                height={20}
                className="ml-2 text-gold-400"
              />
            </p>
          </div>

          <img
            src="/images/product-pages/pricing-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="bg-blue-50 flex flex-col items-center justify-center font-heading lg:mb-24 mb-12 py-12">
        <h3 className="font-black text-3xl text-center">
            The second half of the home inspection
          </h3>
          <h2 className="font-black text-5xl mb-12 text-center">
            How much it costs
          </h2>

          <div className="relative flex flex-col items-center justify-center bg-blue-100 w-80 h-80 rounded-full mb-8">
            <p className="font-black text-9xl text-blue-400">
              <span className="text-5xl">$</span>
              99
            </p>
          </div>

          <div className="flex space-x-8 mb-12">
            <div className="flex flex-col items-center justify-center w-1/2 pr-8 border-r border-gray-400">
              <p>
                <span className="font-black text-4xl">24</span> hour
              </p>
              <p className="uppercase font-light text-sm">turnaround</p>
            </div>

            <div className="flex flex-col items-center justify-center w-1/2">
              <p className="font-black text-4xl">100%</p>
              <p className="uppercase font-light text-sm">GUARANTEED</p>
              <p className="uppercase font-light text-sm">SATISFACTION</p>
            </div>
          </div>

          <p className="max-w-md font-light text-center leading-8 mb-12">
            Receive your repair estimates within 24 of submitting your home
            inspection PDF. If you’re not 100% satisfied with the Domoreport,
            you can request a full refund.
          </p>

          <Link href="/checkout">
            <a>
              <Button variant="primary" clipSide="right">
                Get your repair estimates
              </Button>
            </a>
          </Link>
        </div>

        <FeaturesList
          title="All Included Features"
          url="/demo"
          buttonText="View a sample Domoreport"
        />

        <div className="flex md:flex-row flex-col items-center md:pb-36 pb-12 lg:px-0 px-8">
          <div className="md:w-1/2 w-full text-center">
            <h4 className="font-black text-2xl">
              Interested in discounted pricing?
            </h4>
            <p>We have an affiliate program.</p>
          </div>

          <div className="md:w-1/2 w-full">
            <AffiliateContactForm
              action="https://majordomo.us19.list-manage.com/subscribe/post?u=0d8c6654c6ad1fe275a334234&amp;id=ae28f848a0"
              title="Learn More"
              referrer="Pricing - Learn More"
              redirect={true}
            />
          </div>
        </div>

        <div className="flex flex-col md:mb-36 mb-12 md:py-36 py-12 lg:px-0 px-8 bg-gray-50 lg:space-y-8 space-y-8">
          <div className="flex flex-col items-center w-full text-center">
            <h4 className="font-black lg:text-3xl text-2xl mb-2">
              Get a free Majordomo account
            </h4>
            <p className="sm:max-w-md max-w-sm">
              With a free account, you'll be able to view any Domoreport that's
              shared with you.
            </p>
          </div>

          <div className="flex justify-center w-full">
            <Link href="/signup">
              <a>
                <Button variant="primary" clipSide="right" wide>
                  Sign Up
                  <ArrowRightIcon width={16} height={16} className="ml-2" />
                </Button>
              </a>
            </Link>
          </div>
        </div>

        <div className="flex md:flex-row flex-col items-center md:mb-36 mb-12 lg:px-0 px-8">
          <div className="md:w-1/2 w-full">
            <img
              src="/images/product-pages/pricing-agent.png"
              className="lg:max-w-xl max-w-xs object-contain mx-auto"
            />
          </div>

          <div className="flex flex-col md:w-1/2 w-full lg:p-20 md:p-8 p-12">
            <p className="lg:text-4xl text-2xl font-black lg:max-w-lg max-w-sm mb-8">
              “Majordomo helped me and my buyer negotiate a $7,000 reduction
              after the home inspection!”
            </p>

            <p className="mb-2">
              <span className="font-black text-lg">Garrett Lee</span>, Real
              Estate Agent
            </p>
            <img
              src="/images/product-pages/pricing-logo.png"
              className="w-32"
            />
          </div>
        </div>

        <div className="bg-black lg:mb-36 mb-12 py-24 px-8">
          <h1 className="text-5xl text-white font-black text-center mb-12">
            Frequently Asked Questions
          </h1>

          <div className="flex lg:flex-row flex-col text-white mb-12">
            <div className="lg:w-1/2 w-full space-y-12">
              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  "Can I try the Domoreport before I purchase one?"
                </h4>
                <p className="font-light tracking-wider">
                  Yes. We created a couple of sample reports so you can see
                  exactly what you're ordering. You can check out a{' '}
                  <a
                    href="/demo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    sample report
                  </a>
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  "What are my payment options?"
                </h4>
                <p className="font-light tracking-wider">
                  We use Stripe to securely process transactions. So we accept
                  all major credit cards to pay for the Domoreport
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  “When will I know my Domoreport is ready?”
                </h4>
                <p className="font-light tracking-wider">
                  We know your purchase is time sensitive so we’ll send you an
                  email and text message when it’s ready so you can look at it
                  right away.
                </p>
              </div>
            </div>

            <div className="lg:w-1/2 w-full space-y-12">
              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  “Can I share my Domoreport?”
                </h4>
                <p className="font-light tracking-wider">
                  Absolutely. You can share by adding people during the ordering
                  process and by clicking the share button at the top of the
                  Domoreport.{' '}
                  <a
                    href="https://intercom.help/majordomo/en/articles/3216497-how-do-i-share-my-domoreport"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    Learn more
                  </a>
                  . We also make it easy to{' '}
                  <a
                    href="https://intercom.help/majordomo/en/articles/3301636-how-do-i-read-and-use-the-request-list"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    print and share
                  </a>{' '}
                  the request list.
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  "Do you create Domoreports for multi-family properties?"
                </h4>
                <p className="font-light tracking-wider">
                  Yes, but our pricing and timing are different based on the
                  number of units. If you have a multi-family inspection,{' '}
                  <Link href="/contact-us">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold-400 hover:underline"
                    >
                      contact us
                    </a>
                  </Link>{' '}
                  and we'll discuss the cost and timing for your particular
                  needs.
                </p>
              </div>

              <div className="max-w-md mx-auto">
                <h4 className="text-2xl font-black font-heading mt-8 mb-4">
                  "I'm not very technical. Will I still be able to use the
                  Domoreport?"
                </h4>
                <p className="font-light tracking-wider">
                  There's a comprehensive library of{' '}
                  <a
                    href="https://intercom.help/majordomo/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    frequently asked questions
                  </a>{' '}
                  that will guide you through the Domoreport. There's also a{' '}
                  <a
                    href="https://youtu.be/2zEgrYvo5jE"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-400 hover:underline"
                  >
                    video tour
                  </a>{' '}
                  showing how everything works. And if you get stuck at any
                  point, our team will be happy to assist you at any time.
                </p>
              </div>
            </div>
          </div>

          <div className="mx-auto">
            <p className="font-light text-center text-white leading-8 tracking-wider">
              More questions? Let us help.
              <br />
              <Link href="/contact-us">
                <a className="text-gold-400 hover:underline">Contact us</a>
              </Link>{' '}
              or check out our full{' '}
              <a
                href="https://intercom.help/majordomo/en/"
                className="text-gold-400 hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
              .
            </p>
          </div>
        </div>

        <div className="mb-12">
          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-4 lg:p-16 p-4 lg:w-1/2 w-full flex flex-col md:order-first order-last">
              <div>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  100% satisfaction
                </h4>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  60-day money-back guarantee
                </h4>
              </div>

              <p className="max-w-lg">
                If you don't love the Domoreport, we'll return 100% of your
                money. But we're confident you'll love it. Here’s why:
              </p>

              <p className="max-w-lg">
                Majordomo's repair-estimate data comes from analysts who study
                contractor and insurance invoices for 20,000 projects from
                35,000 providers for material, equipment, labor and installation
                costs across the US. This lets us create highly accurate
                localized estimates for your specific market.{' '}
                <a
                  href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                  className="text-accentBlue hover:underline"
                >
                  Learn More
                </a>
              </p>

              <p className="max-w-lg">
                We invite you to use the Domoreport with your home inspection
                and receive repair estimates within 24 hours. We're confident
                you'll be impressed with its completeness, readability and
                accuracy.
              </p>

              <p className="max-w-lg">
                So here's our offer: If you don’t love the Domoreport or if the
                estimates aren't accurate, we'll refund 100% of your money. You
                have nothing to lose by giving it a try.
              </p>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 md:order-last order-first">
              <img
                src="/images/product-pages/satisfaction.png"
                className="md:max-w-md max-w-xs"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-center md:pb-36 pb-12">
          <Link href="/checkout">
            <a>
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={16} height={16} className="ml-2" />
              </Button>
            </a>
          </Link>
        </div>
      </main>

      <Footer />
    </>
  )
}
